// widget.js: <script src="https://chat.example.com/widget.js" data-bot="pk_..." async></script>
//
// Reads its settings from the script tag (data-*), window.wirefaceChatSettings and any queued calls, then draws the
// launcher. Loading it twice is harmless.
import { createGlobal, VERSION, watchRoutes } from './api.ts';
import type { BootOptions } from './types.ts';

(() => {
  const existing = window.WirefaceChat;
  if (existing && (existing as { version?: string }).version) {
    if (existing.version !== VERSION) {
      console.warn(
        `[Wireface Chat] widget.js ${VERSION} loaded after ${existing.version}; keeping ${existing.version}`,
      );
    }
    return;
  }
  const script =
    (document.currentScript as HTMLScriptElement | null) ??
    document.querySelector<HTMLScriptElement>('script[src*="/widget.js"],script[src*="/widget@"]');
  const data = script?.dataset ?? {};
  const host = data.host ?? (script?.src ? new URL(script.src, location.href).origin : location.origin);
  const nonce = script?.nonce || script?.getAttribute('nonce') || null;

  const registry = createGlobal(host, nonce);
  const queued =
    (existing as { q?: Array<[string, unknown[], (v: unknown) => void, (e: unknown) => void]> } | undefined)?.q ?? [];
  window.WirefaceChat = registry.global;

  // settings from the script tag, then window.wirefaceChatSettings (later wins)
  const fromTag: Partial<BootOptions> = {};
  if (data.bot) fromTag.bot = data.bot;
  if (data.layout) fromTag.layout = data.layout as BootOptions['layout'];
  if (data.theme) fromTag.theme = data.theme as BootOptions['theme'];
  if (data.locale) fromTag.locale = data.locale;
  if (data.target) fromTag.target = data.target;
  if (data.open === 'true') fromTag.open = true;
  if (data.preview) fromTag.preview = data.preview;
  if (data.debug === 'true') fromTag.debug = true;
  if (data.storage) fromTag.storage = data.storage as BootOptions['storage'];
  if (data.launcher || data.position || data.edge || data.align || data.label) {
    fromTag.launcher = {
      ...(data.launcher ? { type: data.launcher as 'bubble' } : {}),
      ...(data.position ? { position: data.position as 'bottom-right' } : {}),
      ...(data.edge ? { edge: data.edge as 'right' } : {}),
      ...(data.align ? { align: data.align as 'end' } : {}),
      ...(data.label ? { label: data.label } : {}),
    };
  }
  const settings = { ...fromTag, ...(window.wirefaceChatSettings ?? {}) } as BootOptions;

  // replay calls made before we loaded (boot first, so the others have a chat to talk to)
  const boots = queued.filter(q => q[0] === 'boot');
  const rest = queued.filter(q => q[0] !== 'boot');
  for (const [, args, resolve, reject] of boots) registry.boot(args[0] as BootOptions, host).then(resolve, reject);
  if (!boots.length && settings.bot && data.autoboot !== 'false') void registry.boot(settings, host);
  for (const [method, args, resolve, reject] of rest) {
    Promise.resolve(
      (registry.global as unknown as (m: string, ...a: unknown[]) => Promise<unknown>)(method, ...args),
    ).then(resolve, reject);
  }

  // any element with data-wireface-open opens the chat (its value can name a bot)
  document.addEventListener('click', e => {
    const opener = (e.target as Element | null)?.closest?.('[data-wireface-open]');
    if (!opener) return;
    e.preventDefault();
    const bot = opener.getAttribute('data-wireface-open');
    const instance = bot ? registry.instances.find(i => i.bot === bot) : registry.instances[0];
    void instance?.open('api');
  });

  // each chat follows route changes unless it was booted with spa: 'manual'
  watchRoutes(() => {
    for (const i of registry.instances) if (i.followsRoutes) i.page();
  });
  window.dispatchEvent(new CustomEvent('wirefacechat:loaded', { detail: { version: VERSION } }));
})();
